/**
 * @author      Shivani Mehra
 * @date        Aug, 2024
 * @copyright   www.ibirdsservices.com
 */

import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Form,
  Container,
  Row,
  Col,
  Card,
  Button,
  Overlay,
  Popover,
} from "react-bootstrap";
import WhatsAppChat from "./WhatsAppChat";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { NameInitialsAvatar } from "react-name-initials-avatar";
import Select from "react-select";
import MessageTemplateModal from "./MessageTemplateModal";
import jwt_decode from "jwt-decode";
import { MultiSelect } from "react-multi-select-component";
import { BsFilter } from "react-icons/bs";
import { FaThumbtack } from "react-icons/fa";
import moment from "moment";
import * as constants from "../../constants/CONSTANT";
import QuickRepliesTemplateModel from "./QuickRepliesTemplateModel";

const WhatsAppMessenger = ({ socket, selectedWhatsAppSetting }) => {
  const [showHide, setShowHide] = useState(false);
  const [userData, setUserData] = useState();
  const [filters, setFilters] = useState({
    textName: "",
    recordType: "recentlyMessage",
  });
  const [receivedMessage, setReceivedMessage] = useState();
  const [showHideModal, setShowHideModal] = useState(false);
  const [userInfo, setUserInfo] = useState(
    jwt_decode(sessionStorage.getItem("token")),
  );
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef(null);
  const hasInteractiveMessageModule =
    userInfo?.modules?.some((module) => module.url === "interactive_message") ||
    false;
  // Helper function to format phone numbers based on user permissions
  const formatPhoneNumber = (number) => {
    const shouldHideNumber =
      userInfo?.userrole !== "ADMIN" &&
      userInfo?.is_whatsapp_number_hidden === true;

    if (shouldHideNumber && number) {
      const numberString = String(number);
      if (numberString.length <= 5) {
        return numberString;
      }
      const visibleDigits = numberString.slice(-5);
      const maskedDigits = "X".repeat(numberString.length - 5);
      return `${maskedDigits}${visibleDigits}`;
    }
    return number;
  };

  const hasTagsModule =
    userInfo?.modules?.some((module) => module.url === "tags") || false;
  const [bgColors, setBgColors] = useState([
    "#d3761f",
    "#00ad5b",
    "#debf31",
    "#239dd1",
    "#b67eb1",
    "#d3761f",
    "#de242f",
  ]);
  const [brokenImages, setBrokenImages] = useState([]);
  let colIndex = 0;
  const [cities, setCities] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const profileImage = "/public/" + userInfo.tenantcode + "/users";
  const [unreadMsgCount, setUnreadMsgCount] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [pinLoading, setPinLoading] = useState({});

  // Tag color palette
  const tagColors = [
    "#e6d0f7",
    "#b7c7ce",
    "#f7e6d0",
    "#d0f7e6",
    "#d0e6f7",
    "#f7d0d0",
    "#f7f3d0",
    "#d0f7f3",
    "#f3d0f7",
    "#d0f3f7",
  ];
  const [activeFilter, setActiveFilter] = useState("All");
  const [allTags, setAllTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagFilterLogic, setTagFilterLogic] = useState("OR");
  const [showTagFilterPopover, setShowTagFilterPopover] = useState(false);
  const filterButtonRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [selectedConversations, setSelectedConversations] = useState([]);
  const [selectAllConversations, setSelectAllConversations] = useState(false);
  const [sendingQuickReplies, setSendingQuickReplies] = useState(false);
  const [showBulkQuickReplyModal, setShowBulkQuickReplyModal] = useState(false);
  // Lazy load: accumulated records from API, then filtered for display (body)
  const [rawAccumulated, setRawAccumulated] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const tableContainerRef = useRef(null);
  const fetchInFlightRef = useRef(false);
  const LAZY_PAGE_SIZE = 50;

  const convertToCSV = (array) => {
    if (!array || array.length === 0) return "";

    const headers = Object.keys(array[0]);
    const csvRows = [
      headers.join(","),
      ...array.map((row) =>
        headers
          .map((field) => {
            const value = row[field]
              ? String(row[field]).replace(/"/g, '""')
              : "";
            return `"${value}"`;
          })
          .join(","),
      ),
    ];
    return csvRows.join("\n");
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      const response = await WhatsAppAPI.exportMessageHistory(
        selectedWhatsAppSetting,
      );

      if (!response.success || !response.records?.length) {
        toast.warning("No records found.");
        setLoading(false);
        return;
      }

      const formatted = response.records
        .filter((msg) => {
          const hasMessage = !!msg.chatmsg?.trim();
          const hasTemplate = !!msg.template_name?.trim();
          const hasFile = !!msg.file_title?.trim();
          return hasMessage || hasTemplate || hasFile;
        })
        .map((msg) => ({
          Name: msg.name,
          To_Number: formatPhoneNumber(msg.to_number),
          Business_Number: msg.business_number,
          Message: msg.chatmsg,
          Template_Name: msg.template_name,
          Status: msg.status,
          Created_Date: moment(msg.createddate).format("DD-MM-YYYY HH:mm:ss"),
          File_URL: msg.file_title
            ? `${constants.FRONTEND_URL}/public/${userInfo.tenantcode}/attachment/${msg.file_title}`
            : "",
        }));

      if (!formatted.length) {
        alert("No valid records to export.");
        setLoading(false);
        return;
      }

      const csv = convertToCSV(formatted);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `message_history_${moment().format("DDMMYYYY_HHmm")}.csv`;
      link.click();
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const getPinId = (item) => {
    if (filters.recordType === "recentlyMessage") {
      return item.lead_id;
    }
    return item._pin_id || item.id;
  };

  const canPin = (item) => {
    if (["lead", "unread"].includes(filters.recordType)) return !!item.id;
    if (filters.recordType === "recentlyMessage") return !!item.lead_id;
    return false;
  };

  const handlePinToggle = async (item) => {
    const pinId = getPinId(item);
    if (!pinId || pinLoading[pinId]) return;
    setPinLoading((prev) => ({ ...prev, [pinId]: true }));

    try {
      if (item.pinned) {
        await WhatsAppAPI.unpinLead(pinId);
      } else {
        await WhatsAppAPI.pinLead(pinId);
      }
      await fetchData();
    } catch (error) {
      console.error("Error toggling pin:", error);
    } finally {
      setPinLoading((prev) => ({ ...prev, [pinId]: false }));
    }
  };

  // Derive displayed list from raw accumulated records + tag filter + format
  const body = React.useMemo(() => {
    const { recordType } = filters;
    let records = rawAccumulated;
    if (
      (recordType === "lead" ||
        recordType === "recentlyMessage" ||
        recordType === "unread") &&
      selectedTags.length > 0
    ) {
      const selectedTagIds = selectedTags.map((tag) => tag.value);
      records = records.filter((record) => {
        const recordTagIds = (record.tag_names || []).map((tag) => tag.id);
        if (tagFilterLogic === "AND") {
          return selectedTagIds.every((tagId) => recordTagIds.includes(tagId));
        }
        return selectedTagIds.some((tagId) => recordTagIds.includes(tagId));
      });
    }
    return records.map((record) => ({
      ...record,
      display_whatsapp_number: formatPhoneNumber(record.whatsapp_number),
    }));
  }, [
    rawAccumulated,
    selectedTags,
    tagFilterLogic,
    filters.recordType,
    userInfo,
  ]);

  // Load next chunk of 50 records
  const loadMore = useCallback(async () => {
    if (!hasMore || isLoadingMore || !filters.recordType) return;
    setIsLoadingMore(true);
    try {
      const result = await WhatsAppAPI.getFilterDataLazy(
        filters.textName,
        filters.recordType,
        LAZY_PAGE_SIZE,
        rawAccumulated.length,
      );
      if (result.success && Array.isArray(result.records)) {
        setRawAccumulated((prev) => [...prev, ...result.records]);
        setTotalCount(result.total ?? 0);
        setHasMore(result.hasMore ?? false);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error loading more:", error);
      setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    hasMore,
    isLoadingMore,
    filters.textName,
    filters.recordType,
    rawAccumulated.length,
  ]);

  // Main data fetching function (initial load or filter/search change)
  const fetchData = useCallback(
    async (isFilterChange = false) => {
      if (fetchInFlightRef.current) return;
      const { textName, recordType } = filters;

      if (!recordType) {
        setRawAccumulated([]);
        setTotalCount(0);
        setHasMore(false);
        return;
      }

      if (isFilterChange) {
        setIsFilterLoading(true);
      } else {
        setIsSearching(true);
      }
      fetchInFlightRef.current = true;

      try {
        const result = await WhatsAppAPI.getFilterDataLazy(
          textName,
          recordType,
          LAZY_PAGE_SIZE,
          0,
        );

        if (result.success && Array.isArray(result.records)) {
          setRawAccumulated(result.records);
          setTotalCount(result.total ?? 0);
          setHasMore(result.hasMore ?? false);
        } else {
          setRawAccumulated([]);
          setTotalCount(0);
          setHasMore(false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setRawAccumulated([]);
        setTotalCount(0);
        setHasMore(false);
      } finally {
        fetchInFlightRef.current = false;
        if (isFilterChange) {
          setIsFilterLoading(false);
        } else {
          setIsSearching(false);
        }
      }
    },
    [filters, selectedWhatsAppSetting],
  );

  // Fetch unread message counts
  const getUnreadMsgCounts = useCallback(async () => {
    try {
      let result = await WhatsAppAPI.fetchUnreadMsgCount(
        selectedWhatsAppSetting,
      );
      if (result.success) {
        setUnreadMsgCount(result.records);
      } else {
        setUnreadMsgCount([]);
      }
    } catch (error) {
      console.error("Error fetching unread counts:", error);
      setUnreadMsgCount([]);
    }
  }, [selectedWhatsAppSetting]);

  // Socket listener for new messages
  useEffect(() => {
    socket?.on("receivedwhatsappmessage", (data) => {
      setReceivedMessage(data);
    });

    return () => {
      socket?.off("receivedwhatsappmessage");
    };
  }, [socket]);

  // Main data fetching effect for initial load and filter changes
  useEffect(() => {
    fetchData(true); // Pass true to indicate it's a filter change
    getUnreadMsgCounts();
  }, [filters.recordType, selectedWhatsAppSetting, receivedMessage]);

  // Debounced search effect
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Don't trigger another fetch for empty search:
    // main fetching effect already handles initial/filter loads.
    if (filters.textName === "") {
      return;
    }

    // Set a timeout to debounce search
    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(() => {
      fetchData(false); // Pass false to indicate it's a search (not filter change)
    }, 500); // 500ms debounce delay

    // Cleanup timeout
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [filters.textName, fetchData]);

  // Initialize record type based on user modules
  useEffect(() => {
    if (!filters.recordType) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        recordType: userInfo?.modules?.some((module) => module.url === "leads")
          ? "lead"
          : "user",
      }));
    }
  }, [filters, userInfo?.modules]);

  // Fetch all tags for filtering
  useEffect(() => {
    const fetchAllTags = async () => {
      try {
        const leadsResult = await WhatsAppAPI.getFilterData("", "lead");
        if (leadsResult.success) {
          const tagsMap = {};
          leadsResult.records.forEach((lead) => {
            (lead.tag_names || []).forEach((tag) => {
              tagsMap[tag.id] = tag.name;
            });
          });
          const uniqueTags = Object.entries(tagsMap).map(([id, name]) => ({
            value: id,
            label: name,
          }));
          setAllTags(uniqueTags);
        }
      } catch (error) {
        console.error("Error fetching tags:", error);
      }
    };

    if (hasTagsModule) {
      fetchAllTags();
    }
  }, [hasTagsModule]);

  const handleFilterButtonClick = useCallback((filterValue) => {
    // Clear data immediately
    setRawAccumulated([]);
    setShowHide(false);
    setSelectedItems([]);
    setSelectAll(false);
    setSelectedTag(null);

    // Clear tags when switching to a filter that doesn't support tags
    if (
      filterValue !== "lead" &&
      filterValue !== "recentlyMessage" &&
      filterValue !== "unread"
    ) {
      setSelectedTags([]);
    }

    // Update filter
    setFilters((f) => ({
      ...f,
      recordType: filterValue,
    }));
  }, []);

  const handleChange = useCallback(async (event) => {
    const { name, value } = event.target;

    // Clear the data first when changing recordType
    if (name === "recordType") {
      setRawAccumulated([]);
      setShowHide(false);
      setSelectedItems([]);
      setSelectAll(false);

      // Clear selected tags when switching away from tag-supporting filters
      if (
        value !== "lead" &&
        value !== "recentlyMessage" &&
        value !== "unread"
      ) {
        setSelectedTags([]);
      }

      setSelectedTag(null);

      // Update filters for recordType change
      setFilters((prevFilters) => ({
        ...prevFilters,
        [name]: value,
      }));
    } else if (name === "textName") {
      // For search, just update the filter without clearing data immediately
      // The debounced effect will handle the actual fetch
      setFilters((prevFilters) => ({
        ...prevFilters,
        [name]: value,
      }));
    }
  }, []);

  const handleUserClick = (item, unreadCount) => {
    setShowHide(true);
    setUserData(item);

    if (item.whatsapp_number && unreadCount > 0) {
      let fullNumber;

      if (filters?.recordType === "recentlyMessage") {
        fullNumber = item.whatsapp_number?.startsWith("+")
          ? item.whatsapp_number
          : `+${item.whatsapp_number}`;
      } else {
        const rawNumber = item.whatsapp_number?.toString().replace(/^\+/, "");
        const countryCode = (item.country_code || "91").replace(/^\+?/, "+");
        fullNumber = `${countryCode}${rawNumber}`;
      }
      markMessagesAsRead(fullNumber);
    }
  };

  const markMessagesAsRead = async (whatsapp_number) => {
    try {
      let result = await WhatsAppAPI.markMessagesAsRead(
        whatsapp_number,
        selectedWhatsAppSetting,
      );
      if (result.success) {
        getUnreadMsgCounts();
      }
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  };

  const handleSelectAll = () => {
    if (!selectAll) {
      setSelectedItems([...body]);
    } else {
      setSelectedItems([]);
    }
    setSelectAll(!selectAll);
  };

  const handleCheckboxChange = (event, item) => {
    if (event.target.checked) {
      setSelectedItems((prevSelected) => [...prevSelected, item]);
    } else {
      setSelectedItems((prevSelected) =>
        prevSelected.filter((selectedItem) => selectedItem.id !== item.id),
      );
    }
  };

  const isAllSelected = selectedItems.length === body.length;

  const sendMessagesBulk = (e) => {
    setShowHide(false);
    if (selectedItems.length > 0) {
      if (selectedItems[0].whatsapp_number) {
        setShowHideModal(true);
      } else {
        if (selectedItems.length === 1) {
          setShowHideModal(true);
        } else {
          toast.error("Please select only one group at a time.");
        }
      }
    } else {
      toast.error("Please select at least one item.");
    }
  };

  const refreshData = () => {
    fetchData(true);
    setShowHideModal(false);
    setSelectedItems([]);
    setSelectAll(false);
  };

  const fillBgColor = () => {
    colIndex += 1;
    if (colIndex >= bgColors.length) colIndex = 0;
    return bgColors[colIndex];
  };

  const handleTagSelection = (selectedOptions) => {
    setSelectedTags(selectedOptions);
    if (
      selectedOptions.length > 0 &&
      filters.recordType !== "lead" &&
      filters.recordType !== "recentlyMessage" &&
      filters.recordType !== "unread"
    ) {
      setFilters((prev) => ({ ...prev, recordType: "lead" }));
    }
  };

  const clearTagFilter = () => {
    setSelectedTags([]);
    setShowTagFilterPopover(false);
  };

  // Determine if we should show loading
  const showLoading = isFilterLoading || isSearching;

  // Handle individual conversation selection
  const handleConversationSelect = (item, event) => {
    event.stopPropagation(); // Prevent row click

    setSelectedConversations((prev) => {
      const exists = prev.some(
        (conv) =>
          conv.id === item.id ||
          (filters.recordType === "recentlyMessage" &&
            conv.lead_id === item.lead_id),
      );

      if (exists) {
        return prev.filter(
          (conv) =>
            conv.id !== item.id &&
            (filters.recordType !== "recentlyMessage" ||
              conv.lead_id !== item.lead_id),
        );
      } else {
        return [...prev, item];
      }
    });
  };

  // Handle select all conversations
  const handleSelectAllConversations = () => {
    if (!selectAllConversations) {
      setSelectedConversations([...body]);
    } else {
      setSelectedConversations([]);
    }
    setSelectAllConversations(!selectAllConversations);
  };

  // Clear all selections
  const clearSelections = () => {
    setSelectedConversations([]);
    setSelectAllConversations(false);
  };

  // Check if a conversation is selected
  const isConversationSelected = (item) => {
    return selectedConversations.some(
      (conv) =>
        conv.id === item.id ||
        (filters.recordType === "recentlyMessage" &&
          conv.lead_id === item.lead_id),
    );
  };

  // Update selectAllConversations when body changes
  useEffect(() => {
    if (selectedConversations.length === body.length && body.length > 0) {
      setSelectAllConversations(true);
    } else {
      setSelectAllConversations(false);
    }
  }, [selectedConversations, body]);

  // Infinite scroll: load more when user scrolls near bottom
  const handleTableScroll = useCallback(() => {
    const el = tableContainerRef.current;
    if (!el || !hasMore || isLoadingMore) return;
    const { scrollTop, clientHeight, scrollHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - 80) {
      loadMore();
    }
  }, [hasMore, isLoadingMore, loadMore]);

  return (
    <>
      <Container className="mt-5">
        <Row className="g-0 mx-5 text-center">
          <Col lg={12} xs={12} sm={12}>
            <div
              className=" text-center p-2"
              style={{
                height: "40px",
                backgroundColor: "#ffffff",
                borderRadius: "5px",
              }}
            >
              <span
                className="fw-semibold p-1"
                style={{ color: "#605C68", fontSize: "large" }}
              >
                WhatsApp for Windows
              </span>
            </div>
          </Col>
        </Row>
      </Container>

      <Container className="mt-2 mb-3">
        <Row className="g-0 mx-5">
          <Col lg={5} sm={12} xs={12} className="mb-3">
            <Card className="h-100">
              <Card.Header className="p-3">
                <Row className="g-0 ">
                  <Col
                    lg={6}
                    xs={6}
                    sm={6}
                    style={{ display: "flex", alignItems: "stretch" }}
                  >
                    <i
                      className="fa-brands fa-whatsapp"
                      style={{ fontSize: "30px", color: "white" }}
                    ></i>
                    <span
                      className="mx-3 mt-1 fw-bold"
                      style={{ fontSize: "15px", color: "white" }}
                    >
                      {userInfo.username}
                    </span>
                  </Col>
                  <Col lg={6} xs={6} sm={6} className="text-end">
                    {selectedConversations.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary me-2"
                        onClick={() => setShowBulkQuickReplyModal(true)}
                        disabled={sendingQuickReplies || showLoading}
                        title={`Send quick reply to ${selectedConversations.length} selected conversation(s)`}
                      >
                        {sendingQuickReplies ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-1"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Sending...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-paper-plane me-1"></i>
                            Send to {selectedConversations.length}
                          </>
                        )}
                      </button>
                    )}
                    {(userInfo.userrole === "ADMIN" ||
                      userInfo.can_export_leads === true) && (
                      <button
                        type="button"
                        className="btn btn-sm btn-light"
                        onClick={handleExport}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Exporting...
                          </>
                        ) : (
                          "Export History"
                        )}
                      </button>
                    )}
                  </Col>
                </Row>
              </Card.Header>

              <Card.Body className="mb-5">
                <div style={{ height: "30rem" }}>
                  <Row className="g-0">
                    <Col lg={12} sm={12} xs={12}>
                      <Row className="mt-2">
                        <Col lg={12} xs={12} sm={12}>
                          <Form.Group className="mb-3">
                            <Form.Control
                              style={{ height: "38px" }}
                              type="text"
                              name="textName"
                              placeholder="Search by name or WhatsApp number..."
                              onChange={handleChange}
                              value={filters.textName}
                              disabled={isFilterLoading} // Only disable for filter changes, not search
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Col>

                    {/* Filter bar */}
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        marginBottom: "10px",
                        alignItems: "center",
                        overflowX: "auto",
                        overflowY: "hidden",
                        whiteSpace: "nowrap",
                        paddingBottom: "5px",
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                      }}
                    >
                      {[
                        { label: "All", value: "lead" },
                        { label: "New Messages", value: "recentlyMessage" },
                        { label: "Unread", value: "unread" },
                        { label: "archived", value: "archived" },
                      ].map((filter) => (
                        <button
                          key={filter.value}
                          type="button"
                          className={`btn btn-sm ${
                            filters.recordType === filter.value && !selectedTag
                              ? "btn-primary"
                              : "btn-outline-secondary"
                          }`}
                          style={{
                            borderRadius: "5px",
                            minWidth:
                              filter.value === "recentlyMessage"
                                ? "120px"
                                : "80px",
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                          }}
                          onClick={() => handleFilterButtonClick(filter.value)}
                          disabled={isFilterLoading} // Only disable for filter changes
                        >
                          {filter.label}
                          {isFilterLoading &&
                            filters.recordType === filter.value && (
                              <span
                                className="ms-2 spinner-border spinner-border-sm"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </span>
                            )}
                        </button>
                      ))}

                      {hasTagsModule && (
                        <div style={{ position: "relative" }}>
                          <button
                            type="button"
                            className={`btn btn-sm ${
                              selectedTags.length > 0
                                ? "btn-primary"
                                : "btn-outline-secondary"
                            }`}
                            style={{
                              borderRadius: "5px",
                              minWidth: "80px",
                              fontWeight: 500,
                              display: "flex",
                              alignItems: "center",
                            }}
                            ref={filterButtonRef}
                            onClick={() =>
                              setShowTagFilterPopover(!showTagFilterPopover)
                            }
                            disabled={isFilterLoading}
                          >
                            <BsFilter style={{ marginRight: 2 }} />
                            {selectedTags.length > 0
                              ? `${selectedTags.length} Tags`
                              : "Filter"}
                          </button>
                        </div>
                      )}

                      {selectedTags.length > 0 &&
                        selectedTags.map((tag) => (
                          <span
                            key={tag.value}
                            className="badge bg-light text-dark border"
                            style={{
                              borderRadius: "0.375rem",
                              padding: "4px 8px",
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              backgroundColor: "#f0f0f0",
                              border: "1px solid #ccc",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {tag.label}
                            <button
                              type="button"
                              className="btn btn-sm p-0"
                              style={{
                                border: "none",
                                background: "none",
                                fontSize: "10px",
                                color: "#666",
                                cursor: "pointer",
                                padding: "0",
                                marginLeft: "2px",
                              }}
                              onClick={() => {
                                setSelectedTags((prev) =>
                                  prev.filter((t) => t.value !== tag.value),
                                );
                              }}
                              title="Remove tag"
                              disabled={isFilterLoading}
                            >
                              ×
                            </button>
                          </span>
                        ))}

                      {selectedTags.length > 0 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          style={{
                            fontSize: "0.75rem",
                            padding: "2px 6px",
                            borderRadius: "0.375rem",
                            whiteSpace: "nowrap",
                          }}
                          onClick={clearTagFilter}
                          title="Clear all tags"
                          disabled={isFilterLoading}
                        >
                          Clear All
                        </button>
                      )}
                    </div>

                    <hr />

                    <div
                      ref={tableContainerRef}
                      className="table-container"
                      style={{ maxHeight: "25rem", overflowY: "auto" }}
                      onScroll={handleTableScroll}
                    >
                      <Col lg={12} sm={12} xs={12}>
                        {showLoading ? (
                          <div className="text-center py-5">
                            <div
                              className="spinner-border text-primary"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <p className="mt-2">
                              {isFilterLoading
                                ? "Loading conversations..."
                                : "Searching..."}
                            </p>
                          </div>
                        ) : body.length > 0 ? (
                          <table className="table">
                            <thead>
                              <tr>
                                {hasInteractiveMessageModule && (
                                  <th>
                                    <Form.Check
                                      type="checkbox"
                                      checked={
                                        selectAllConversations &&
                                        body.length > 0
                                      }
                                      onChange={handleSelectAllConversations}
                                      disabled={
                                        showLoading || body.length === 0
                                      }
                                      title="Select all"
                                    />
                                  </th>
                                )}
                                <th className="px-2">Conversations</th>
                                <th className="px-5"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {body.map((item) => {
                                let fullNumber;

                                if (filters?.recordType === "recentlyMessage") {
                                  fullNumber = item.whatsapp_number?.startsWith(
                                    "+",
                                  )
                                    ? item.whatsapp_number
                                    : `+${item.whatsapp_number}`;
                                } else {
                                  const rawNumber = item.whatsapp_number
                                    ?.toString()
                                    .replace(/^\+/, "");
                                  const countryCode = (
                                    item.country_code || "91"
                                  ).replace(/^\+?/, "+");
                                  fullNumber = `${countryCode}${rawNumber}`;
                                }

                                const unreadCount = unreadMsgCount.find(
                                  (unread) =>
                                    unread.whatsapp_number === fullNumber,
                                )?.unread_msg_count;

                                return (
                                  <tr
                                    key={
                                      filters.recordType === "recentlyMessage"
                                        ? item.lead_id || item.id
                                        : item.id
                                    }
                                  >
                                    {hasInteractiveMessageModule && (
                                      <td>
                                        <Form.Check
                                          type="checkbox"
                                          checked={isConversationSelected(item)}
                                          onChange={(e) =>
                                            handleConversationSelect(item, e)
                                          }
                                          disabled={showLoading}
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      </td>
                                    )}
                                    <td
                                      onClick={() =>
                                        handleUserClick(item, unreadCount)
                                      }
                                      style={{ cursor: "pointer" }}
                                      className="d-flex align-items-center text-uppercase px-3"
                                    >
                                      {brokenImages.includes(
                                        `img-${item?.id}`,
                                      ) ? (
                                        <NameInitialsAvatar
                                          size="30px"
                                          textSize="13px"
                                          bgColor={fillBgColor()}
                                          borderWidth="0px"
                                          textColor="#fff"
                                          name={item?.contactname}
                                        />
                                      ) : (
                                        <img
                                          alt=""
                                          style={{
                                            height: "30px",
                                            width: "30px",
                                            objectFit: "cover",
                                          }}
                                          src={profileImage + "/" + item.id}
                                          className="rounded-circle"
                                          onError={() =>
                                            setBrokenImages((prev) => [
                                              ...prev,
                                              `img-${item?.id}`,
                                            ])
                                          }
                                          id={`img-${item?.id}`}
                                        />
                                      )}
                                      <span
                                        className="mx-3 mt-1 text-capitalize"
                                        style={{ fontSize: "15px" }}
                                      >
                                        {item.contactname}
                                        {item?.whatsapp_number && (
                                          <i
                                            className="mx-2"
                                            style={{ fontSize: "10px" }}
                                          >
                                            ({" "}
                                            {item?.country_code
                                              ? `${item.country_code}${
                                                  item.display_whatsapp_number ||
                                                  formatPhoneNumber(
                                                    item.whatsapp_number,
                                                  )
                                                }`
                                              : item.display_whatsapp_number ||
                                                formatPhoneNumber(
                                                  item.whatsapp_number,
                                                )}{" "}
                                            )
                                          </i>
                                        )}
                                      </span>
                                    </td>
                                    <td className="pt-3">
                                      {unreadCount && unreadCount > 0 && (
                                        <span
                                          className="badge bg-success rounded-circle float-end mx-2"
                                          style={{ fontSize: "10px" }}
                                        >
                                          {unreadCount}
                                        </span>
                                      )}
                                    </td>
                                    {[
                                      "lead",
                                      "recentlyMessage",
                                      "unread",
                                    ].includes(filters.recordType) &&
                                      canPin(item) && (
                                        <td className="pt-3">
                                          <Button
                                            variant="link"
                                            className="p-0 m-0"
                                            style={{
                                              color: item.pinned
                                                ? "#007bff"
                                                : "#aaa",
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handlePinToggle(item);
                                            }}
                                            disabled={
                                              pinLoading[getPinId(item)] ||
                                              showLoading
                                            }
                                            title={
                                              item.pinned ? "Unpin" : "Pin"
                                            }
                                          >
                                            <FaThumbtack
                                              style={
                                                item.pinned
                                                  ? {}
                                                  : { opacity: 0.5 }
                                              }
                                            />
                                          </Button>
                                        </td>
                                      )}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        ) : null}
                        {!showLoading && body.length > 0 && hasMore && (
                          <div className="text-center py-2 border-top">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={loadMore}
                              disabled={isLoadingMore}
                            >
                              {isLoadingMore ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-1"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                  Loading...
                                </>
                              ) : (
                                "Load more"
                              )}
                            </button>
                          </div>
                        )}
                        {!showLoading && body.length === 0 ? (
                          <div className="text-center py-5">
                            {filters.textName
                              ? "No conversations found matching your search."
                              : "No conversations found."}
                          </div>
                        ) : null}
                      </Col>
                    </div>
                  </Row>
                </div>
              </Card.Body>

              <Card.Footer>
                <Row className="g-0 mt-3">
                  <Col lg={6} sm={12} xs={12} className="mt-1">
                    <p>
                      <b>
                        {hasMore || rawAccumulated.length !== totalCount
                          ? `Showing ${body.length} of ${totalCount}`
                          : `Total Records: ${body.length}`}
                      </b>
                    </p>
                  </Col>
                </Row>
              </Card.Footer>
            </Card>
          </Col>

          {showHide && (
            <Col lg={7} sm={12} xs={12} className="mb-3">
              <WhatsAppChat
                show={showHide}
                onHide={() => setShowHide(false)}
                userDetail={userData}
                filterData={filters}
                socket={socket}
                selectedWhatsAppSetting={selectedWhatsAppSetting}
                refreshDataMessageList={refreshData}
              />
            </Col>
          )}
        </Row>
      </Container>
      <ToastContainer />
      {/* Bulk Quick Reply Modal */}
      {showBulkQuickReplyModal && selectedConversations.length > 0 && (
        <QuickRepliesTemplateModel
          show={showBulkQuickReplyModal}
          onHide={() => {
            setShowBulkQuickReplyModal(false);
            clearSelections();
          }}
          contactData={selectedConversations}
          refreshData={refreshData}
          filterData={filters}
          selectedWhatsAppSetting={selectedWhatsAppSetting}
          isBulk={true}
          onBulkSendComplete={() => {
            setSendingQuickReplies(false);
            clearSelections();
          }}
        />
      )}
      {showHideModal && (
        <MessageTemplateModal
          show={showHideModal}
          onHide={() => setShowHideModal(false)}
          contactData={selectedItems}
          refreshData={refreshData}
          filterData={filters}
          selectedWhatsAppSetting={selectedWhatsAppSetting}
        />
      )}

      <Overlay
        show={showTagFilterPopover}
        target={filterButtonRef.current}
        placement="bottom"
        containerPadding={20}
        rootClose
        onHide={() => setShowTagFilterPopover(false)}
        trigger="click"
      >
        <Popover id="tag-filter-popover" style={{ minWidth: 400 }}>
          <Popover.Header
            as="h3"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Filter Leads by Tags</span>
            <div>
              {selectedTags.length > 0 && (
                <Button
                  size="sm"
                  variant="link"
                  style={{
                    color: "#dc3545",
                    textDecoration: "none",
                    marginRight: "10px",
                  }}
                  onClick={clearTagFilter}
                  disabled={isFilterLoading}
                >
                  Clear
                </Button>
              )}
              <Button
                size="sm"
                variant="link"
                style={{ color: "#000", textDecoration: "none" }}
                onClick={() => setShowTagFilterPopover(false)}
              >
                X
              </Button>
            </div>
          </Popover.Header>
          <Popover.Body style={{ minWidth: 400 }}>
            <div style={{ minWidth: 350, maxWidth: "100%" }}>
              <MultiSelect
                options={allTags}
                value={selectedTags}
                onChange={handleTagSelection}
                labelledBy="Select Tags"
                overrideStrings={{
                  selectSomeItems: "Select tags...",
                  allItemsAreSelected: "All tags selected",
                  selectAll: "Select All",
                  search: "Search tags",
                }}
                style={{ minWidth: 350, maxWidth: "100%" }}
                disabled={isFilterLoading}
              />
            </div>
            <div className="mt-3">
              <Form.Check
                inline
                label="OR"
                type="radio"
                checked={tagFilterLogic === "OR"}
                onChange={() => setTagFilterLogic("OR")}
                disabled={isFilterLoading}
              />
              <Form.Check
                inline
                label="AND"
                type="radio"
                checked={tagFilterLogic === "AND"}
                onChange={() => setTagFilterLogic("AND")}
                disabled={isFilterLoading}
              />
            </div>
            {selectedTags.length > 0 && (
              <div className="mt-3">
                <strong>Selected Tags:</strong>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    marginTop: "8px",
                  }}
                >
                  {selectedTags.map((tag) => (
                    <span
                      key={tag.value}
                      className="badge bg-light text-dark border"
                      style={{
                        borderRadius: "0.375rem",
                        padding: "4px 8px",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        backgroundColor: "#f0f0f0",
                        border: "1px solid #ccc",
                        display: "flex",
                        alignItems: "center",
                        marginRight: 4,
                      }}
                    >
                      {tag.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Popover.Body>
        </Popover>
      </Overlay>
    </>
  );
};

export default WhatsAppMessenger;
